import {inject, Injectable} from '@angular/core';
import {PaginatedListData} from '../../models/paginated-list-data.model';
import {ListType} from '../../types/list/list-type';
import {PaginatedList} from '../../util/paginated-list';
import {BehaviorSubject, firstValueFrom, fromEvent, Subscription} from 'rxjs';
import {take} from 'rxjs/operators';
import {ListDataService} from '../list-data/list-data.service';
import {ListDataQuery} from '../../queries/list-data.query';
import {formatNumber} from '@angular/common';
import {ListItem} from '../../models/list/list-item.model';
import {TooltipVersion} from '../../types/tooltip/tooltip-version.interface';
import {TooltipInProgress} from '../../types/tooltip/tooltip-in-progress.interface';
import {AppService} from '../app/app.service';
import {Vector2D} from '../../models/vector-2d.model';
import {ListItemTypeEnum} from '../../enums/list-item/list-item-type.enum';
import {DateMappingService} from '../mapping/date-mapping/date-mapping.service';
import {SupportedLocalesEnum} from '../../enums/supported-locales.enum';

@Injectable({
    providedIn: 'root'
})
export class ListService {
    public latestList$: BehaviorSubject<PaginatedList<unknown> | undefined>;
    private readonly lists$: BehaviorSubject<Record<string, PaginatedList<unknown>>>;
    private readonly listDataService = inject(ListDataService);
    private readonly listDataQuery = inject(ListDataQuery);
    private readonly appService = inject(AppService);
    private readonly dateMappingService = inject(DateMappingService);

    public constructor() {
        this.lists$ = new BehaviorSubject<Record<string, PaginatedList<unknown>>>({});
        this.latestList$ = new BehaviorSubject<PaginatedList<unknown> | undefined>(undefined);
    }

    public createList<T>(name: string, listType: ListType, limit: number = 40, useCache: boolean = true, idKey?: string): PaginatedList<T> {
        const listData = this.listDataQuery.getPaginatedListData(name);
        const list: PaginatedList<T> | undefined = new PaginatedList<T>(listType, limit, idKey, this.listDataService, this);
        this.prepareList(list, name, useCache, listData);
        this.latestList$.next(list as PaginatedList<unknown>);
        const lists = this.lists$.getValue();
        lists[name] = list as PaginatedList<unknown>;
        this.lists$.next(lists);
        return list;
    }

    public getList<T>(name: string): PaginatedList<T> | undefined {
        const lists = this.lists$.getValue();
        if (lists[name]) {
            this.latestList$.next(lists[name]);
            return lists[name] as PaginatedList<T>;
        }
        return undefined;
    }

    public getOrCreateList<T>(listName: string, listType: ListType, limit?: number, cache?: boolean, idKey?: string): PaginatedList<T> {
        let list = this.getList<T>(listName);
        if (!list) {
            list = this.createList<T>(listName, listType, limit, cache, idKey);
        }
        return list;
    }

    public resetListStorage(): void {
        this.listDataService.resetListStorage();
    }

    public setListActive(listName: string, newList: PaginatedList<unknown>): void {
        const lists = this.lists$.getValue();
        newList.activated = new Date();
        if (lists && !(listName in lists)) {
            lists[listName] = newList;
            this.listDataService.setListActive(listName, newList.activated);
            this.lists$.next(lists);
        }
    }

    public cleanActiveListsFromStore(): void {
        this.listDataService.resetListActiveState();
        this.lists$.next({});
    }

    public async getLatestList(): Promise<PaginatedList<unknown> | undefined> {
        return firstValueFrom(this.latestList$.pipe(take(1)));
    }

    public async markItem(identifier: string): Promise<void> {
        const lists = Object.values(this.lists$.getValue());
        for (const list of lists) {
            const listNames = await list.getIds();
            if (listNames.includes(identifier)) {
                list.markItem(identifier, false);
            }
        }
    }

    public async deleteItem(identifier: string): Promise<void> {
        const lists = Object.values(this.lists$.getValue());
        for (const list of lists) {
            const listNames = await list.getIds();
            if (listNames.includes(identifier)) {
                list.setItemDeleted(identifier, false);
            }
        }
    }

    public getListItemFormattedFileVersion(fileVersionNumber: number): string | undefined {
        if (Number.isNaN(fileVersionNumber)) {
            return undefined;
        }

        return formatNumber(fileVersionNumber, SupportedLocalesEnum.EnGb, '1.1-3');
    }

    public getListItemFormattedVersion(version: string): string | undefined {
        const versionNumber = Number(version);
        if (Number.isNaN(versionNumber)) {
            return undefined;
        }

        return formatNumber(versionNumber, SupportedLocalesEnum.EnGb, '1.1-3');
    }

    public getVersionTooltipDataByListItem(listItem: ListItem): TooltipVersion | undefined {
        if (!listItem.versionFromDate || !listItem.versionByUserFullName || !listItem.fileVersion) {
            return undefined;
        }

        const version = this.getListItemFormattedVersion(listItem.fileVersion.toString());
        if (version === undefined) {
            return undefined;
        }

        return {
            date: this.getFormattedDateForListItem(listItem.versionFromDate as Date),
            user: listItem.versionByUserFullName as string,
            version: version,
        };
    }

    public getInProgressTooltipDataByListItem(listItem: ListItem): TooltipInProgress | undefined {
        if (!listItem.inProgressSinceDate || !listItem.inProgressByUserFullName) {
            return undefined;
        }

        return {
            since: this.getFormattedDateForListItem(listItem.inProgressSinceDate as Date),
            checkOutBy: listItem.inProgressByUserFullName as string,
        };
    }

    public getFormattedDateForListItem(date: Date): string {
        return this.dateMappingService.mapDateToShortLocaleDateTimeString(date);
    }

    public setNextListView(listName: string, currentListView: ListItemTypeEnum, allowedListTypes: Array<ListItemTypeEnum>, fullPageRequest: (() => void) | undefined): boolean {
        const steps = [...allowedListTypes];
        const maxStepIndex = steps.length - 1;
        const fullPageIndex = steps.indexOf(ListItemTypeEnum.FullPage);
        if (fullPageIndex != -1) {
            steps.splice(fullPageIndex, 1);
        }
        const currentStepIndex = steps.indexOf(currentListView);
        let nextStep;
        if (currentStepIndex != -1) {
            if (currentStepIndex + 1 < maxStepIndex) {
                nextStep = steps[currentStepIndex + 1];
            } else {
                if (fullPageIndex !== -1 && fullPageRequest) {
                    fullPageRequest();
                } else {
                    nextStep = steps[currentStepIndex + 1];
                }
            }
        }
        if (nextStep) {
            this.appService.setListView(listName, nextStep);
        }
        return false;
    }

    public setPreviousListView(listName: string, currentListView: ListItemTypeEnum, allowedListTypes: Array<ListItemTypeEnum>): void {
        const steps = [...allowedListTypes];
        const fullPageIndex = steps.indexOf(ListItemTypeEnum.FullPage);
        if (fullPageIndex != -1) {
            steps.splice(fullPageIndex, 1);
        }
        const currentStepIndex = steps.indexOf(currentListView);
        if (currentStepIndex != -1) {
            if (currentStepIndex - 1 > -1) {
                this.appService.setListView(listName, steps[currentStepIndex - 1]);
            }
        }
    }

    public getMouseZoomSubscription(listElement: HTMLElement, nextFunction: () => void, previousFunction: () => void): Subscription {
        return fromEvent<WheelEvent>(listElement, 'wheel', { passive: false })
            .subscribe(wheelEvent => {
                if (wheelEvent.ctrlKey) {
                    wheelEvent.preventDefault();
                    if (Math.abs(wheelEvent.deltaY) > 10) {
                        if (wheelEvent.deltaY < 0) {
                            nextFunction();
                        } else {
                            previousFunction();
                        }
                    }
                }
            });
    }

    public getPinchToZoomSubscriptions(listElement: HTMLElement, nextFunction: () => void, previousFunction: () => void): Subscription {
        const subscriptions: Subscription = new Subscription();
        let pointers: Record<string, Vector2D> = {};
        let startDistance: number | undefined;
        const getPointerDistance = (): number | undefined => {
            const pointerIds = Object.keys(pointers);
            if (pointerIds.length > 1) {
                const p1 = pointers[pointerIds[0]];
                const p2 = pointers[pointerIds[1]];
                const a2 = Math.pow(p1.x - p2.x, 2);
                const b2 = Math.pow(p1.y - p2.y, 2);
                return Math.sqrt(a2 + b2);
            }
            return undefined;
        };
        const clearPointers = (e: PointerEvent): void => {
            try {
                startDistance = undefined;
                if (e.pointerId in pointers) {
                    delete pointers[e.pointerId];
                }
            } catch (error) {
                console.error(error);
            }
        };
        subscriptions.add(fromEvent<PointerEvent>(listElement, 'pointerdown', { passive: false })
            .subscribe((e: PointerEvent) => {
                if (!(e.pointerId in pointers)) {
                    pointers[e.pointerId] = {
                        x: e.screenX,
                        y: e.screenY
                    };
                }
                if (startDistance === undefined) {
                    const distance = getPointerDistance();
                    if (distance) {
                        startDistance = distance;
                    }
                }
            }));
        subscriptions.add(fromEvent<PointerEvent>(listElement, 'pointermove', { passive: false })
            .subscribe((e: PointerEvent) => {
                if (e.pointerId in pointers) {
                    pointers[e.pointerId].x = e.screenX;
                    pointers[e.pointerId].y = e.screenY;
                }
                const distance = getPointerDistance();
                if (distance && startDistance) {
                    const pinchDistance = distance - startDistance;
                    const absPinchDistance = Math.abs(pinchDistance);
                    if (absPinchDistance > 100) {
                        e.preventDefault();
                        if (pinchDistance < 0) {
                            nextFunction();
                        } else {
                            previousFunction();
                        }
                        pointers = {};
                    }
                }
            }));
        subscriptions.add(fromEvent<PointerEvent>(listElement, 'pointercancel', { passive: false })
            .subscribe((e: PointerEvent) => {
                clearPointers(e);
            }));
        subscriptions.add(fromEvent<PointerEvent>(listElement, 'pointerup', { passive: false })
            .subscribe((e: PointerEvent) => {
                clearPointers(e);
            }));
        return subscriptions;
    }

    private prepareList<T>(list: PaginatedList<T>, name: string, useCache: boolean, listData?: PaginatedListData): void {
        list.setCaching(useCache);
        list.setName(name);
        list.setPreFilterFunction((dataList): Promise<Array<T>> => Promise.resolve(dataList));
        if (listData && useCache) {
            list.setData(listData.amount, JSON.parse(JSON.stringify(listData.data)), listData.marked, listData.deleted, listData.settings, listData.showRefreshListToast);
        }
        if (useCache) {
            this.setListActive(name, list as PaginatedList<unknown>);
        }
    }
}
